import {
  animate,
  motion,
  useInView,
  useMotionValue,
  useReducedMotion,
  useTransform,
} from "motion/react";
import { useEffect, useRef } from "react";
import { EASE } from "./tokens";

const defaultFormat = (n: number) =>
  new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(Math.round(n));

type CountUpProps = {
  value: number;
  /** seconds (default 1.1) */
  duration?: number;
  /** seconds before starting (default 0) */
  delay?: number;
  /** format the displayed number (default: en-US thousands separators, integer) */
  format?: (n: number) => string;
  /** wait until scrolled into view before counting (default false → counts on mount) */
  startOnView?: boolean;
  className?: string;
};

/**
 * Counts to `value`, animating from the previous value. On mount it runs from
 * 0 (above-fold) or waits until scrolled into view (`startOnView=true`); when
 * `value` changes afterward — e.g. the report's date range / comparison /
 * granularity changes — it animates from the prior value to the new one, which
 * is the "data changed" feedback this component exists to give on the dashboard.
 *
 * Reduced motion → renders the final value with no animation. The animating
 * digits are aria-hidden; a visually-hidden final value carries the accessible
 * reading so screen readers aren't spammed with intermediate numbers.
 */
export function CountUp({
  value,
  duration = 1.1,
  delay = 0,
  format = defaultFormat,
  startOnView = false,
  className,
}: CountUpProps) {
  const reduced = useReducedMotion();
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, amount: 0.4 });
  const count = useMotionValue(0);
  const text = useTransform(count, (latest) => format(latest));

  const shouldRun = startOnView ? inView : true;

  useEffect(() => {
    if (reduced) {
      count.set(value);
      return;
    }
    if (!shouldRun) return;
    // Animate from wherever the MotionValue currently sits — 0 on first run,
    // or the previously-settled value when `value` changes — so subsequent
    // data changes read as a count *to* the new figure, not a reset from 0.
    const controls = animate(count, value, { duration, delay, ease: EASE });
    return () => controls.stop();
  }, [reduced, shouldRun, value, duration, delay, count]);

  return (
    <span ref={ref} className={className}>
      <motion.span aria-hidden="true">{text}</motion.span>
      <span className="sr-only">{format(value)}</span>
    </span>
  );
}
